import React from 'react';
import { Github, X } from 'lucide-react';
import { useLanguageStore } from '../store/languageStore';

const translations = {
  EN: {
    tagline: "AI-powered emergency assistance at your fingertips.",
    quickLinks: "Quick Links",
    about: "About",
    contact: "Contact",
    contactTitle: "Contact",
    contactDescription: "Send me a message and I'll get back to you as soon as possible.",
    emailPlaceholder: "Your email",
    messagePlaceholder: "Your message",
    send: "Send",
    thankYou: "Thank you! Your message has been sent.",
    copyright: "© {year} RightNowAid. All rights reserved.",
    aboutName: "Nelson Rueda",
    aboutTitle: "About",
    aboutRole: "Aspiring Software Engineer, Founder, & CS student @ ELAC",
    aboutDescription: "Driven by personal transformation and a mission to help communities in need—my work builds bridges between real-world struggles and digital support tools.",
    aboutPoints: [
      "Raised in a low-income immigrant household of 6 in East Los Angeles, I experienced food, eviction, and housing instability firsthand.",
      "Motivated by empathy, I have built RightNowAid—a platform that connects users to emergency aid in their ZIP code.",
      "Actively studying programming, full‑stack web dev & AI, planning on transferring to UCLA CS (Fall 2027), and actively preparing for a 2026 Google-STEP internship."
    ]
  },
  ES: {
    tagline: "Asistencia de emergencia impulsada por IA al alcance de tu mano.",
    quickLinks: "Enlaces Rápidos",
    about: "Acerca de",
    contact: "Contacto",
    contactTitle: "Contacto",
    contactDescription: "Envíame un mensaje y te responderé lo antes posible.",
    emailPlaceholder: "Tu correo electrónico",
    messagePlaceholder: "Tu mensaje",
    send: "Enviar",
    thankYou: "¡Gracias! Tu mensaje ha sido enviado.",
    copyright: "© {year} RightNowAid. Todos los derechos reservados.",
    aboutName: "Nelson Rueda",
    aboutTitle: "Acerca de",
    aboutRole: "Ingeniero de Software Aspirante, Fundador y Estudiante de CS en ELAC",
    aboutDescription: "Impulsado por la transformación personal y una misión de ayudar a comunidades necesitadas—mi trabajo construye puentes entre las luchas del mundo real y las herramientas de apoyo digital.",
    aboutPoints: [
      "Criado en un hogar inmigrante de bajos ingresos de 6 personas en el Este de Los Ángeles, experimenté la inseguridad alimentaria, desalojo y vivienda de primera mano.",
      "Motivado por la empatía, he construido RightNowAid—una plataforma que conecta a los usuarios con ayuda de emergencia en su código postal.",
      "Activamente estudiando programación, desarrollo web full-stack e IA, planeando transferirme a UCLA CS (Otoño 2027), y preparándome activamente para una pasantía Google-STEP 2026."
    ]
  },
  ZH: {
    tagline: "AI驱动的紧急援助触手可及。",
    quickLinks: "快速链接",
    about: "关于",
    contact: "联系",
    contactTitle: "联系",
    contactDescription: "给我发送消息，我会尽快回复您。",
    emailPlaceholder: "您的邮箱",
    messagePlaceholder: "您的消息",
    send: "发送",
    thankYou: "谢谢！您的消息已发送。",
    copyright: "© {year} RightNowAid. 保留所有权利。",
    aboutName: "Nelson Rueda",
    aboutTitle: "关于",
    aboutRole: "软件工程师实习生，创始人，ELAC计算机科学学生",
    aboutDescription: "受个人转变和帮助有需要社区的使命驱动—我的工作架起了现实世界困境和数字支持工具之间的桥梁。",
    aboutPoints: [
      "在洛杉矶东部一个6口之家的低收入移民家庭长大，我亲身经历了食品、驱逐和住房不稳定的问题。",
      "在同理心的驱使下，我创建了RightNowAid—一个将用户与其邮政编码区域的紧急援助连接起来的平台。",
      "正在积极学习编程、全栈Web开发和AI，计划转学到UCLA计算机科学专业（2027年秋季），并积极准备2026年Google-STEP实习。"
    ]
  },
  VI: {
    tagline: "Hỗ trợ khẩn cấp được hỗ trợ bởi AI trong tầm tay của bạn.",
    quickLinks: "Liên Kết Nhanh",
    about: "Giới Thiệu",
    contact: "Liên Hệ",
    contactTitle: "Liên Hệ",
    contactDescription: "Gửi tin nhắn cho tôi và tôi sẽ phản hồi sớm nhất có thể.",
    emailPlaceholder: "Email của bạn",
    messagePlaceholder: "Tin nhắn của bạn",
    send: "Gửi",
    thankYou: "Cảm ơn! Tin nhắn của bạn đã được gửi.",
    copyright: "© {year} RightNowAid. Đã đăng ký bản quyền.",
    aboutName: "Nelson Rueda",
    aboutTitle: "Giới Thiệu",
    aboutRole: "Kỹ sư phần mềm tương lai, Người sáng lập, & Sinh viên CS tại ELAC",
    aboutDescription: "Được thúc đẩy bởi sự chuyển đổi cá nhân và sứ mệnh giúp đỡ cộng đồng cần thiết—công việc của tôi xây dựng cầu nối giữa những khó khăn thực tế và công cụ hỗ trợ kỹ thuật số.",
    aboutPoints: [
      "Lớn lên trong một gia đình nhập cư thu nhập thấp 6 người ở Đông Los Angeles, tôi đã trải nghiệm trực tiếp về tình trạng mất an toàn thực phẩm, bị trục xuất và bất ổn nhà ở.",
      "Được thúc đẩy bởi sự đồng cảm, tôi đã xây dựng RightNowAid—một nền tảng kết nối người dùng với hỗ trợ khẩn cấp trong mã bưu điện của họ.",
      "Đang tích cực học lập trình, phát triển web full-stack & AI, dự định chuyển đến UCLA CS (Mùa thu 2027), và đang chuẩn bị cho kỳ thực tập Google-STEP 2026."
    ]
  },
  FIL: {
    tagline: "AI-powered emergency assistance sa iyong mga daliri.",
    quickLinks: "Quick Links",
    about: "Tungkol",
    contact: "Kontak",
    contactTitle: "Kontak",
    contactDescription: "Magpadala ng mensahe sa akin at babalikan kita sa lalong madaling panahon.",
    emailPlaceholder: "Iyong email",
    messagePlaceholder: "Iyong mensahe",
    send: "Ipadala",
    thankYou: "Salamat! Naipadala na ang iyong mensahe.",
    copyright: "© {year} RightNowAid. Lahat ng karapatan ay nakalaan.",
    aboutName: "Nelson Rueda",
    aboutTitle: "Tungkol",
    aboutRole: "Aspiring Software Engineer, Founder, & CS student sa ELAC",
    aboutDescription: "Hinihimok ng personal na pagbabago at misyon na tulungan ang mga komunidad na nangangailangan—ang aking trabaho ay nagtatayo ng mga tulay sa pagitan ng mga tunay na hamon at digital na suporta.",
    aboutPoints: [
      "Pinalaki sa isang mababang-kita na imigrante na pamilya ng 6 sa East Los Angeles, naranasan ko ang kawalan ng katiyakan sa pagkain, pagpapaalis, at pabahay sa unang kamay.",
      "Hinihimok ng empatiya, itinayo ko ang RightNowAid—isang platform na kumokonekta sa mga user sa emergency aid sa kanilang ZIP code.",
      "Aktibong nag-aaral ng programming, full-stack web dev & AI, nagpaplano na lumipat sa UCLA CS (Fall 2027), at aktibong naghahanda para sa 2026 Google-STEP internship."
    ]
  },
  KO: {
    tagline: "AI 기반 긴급 지원이 손끝에 있습니다.",
    quickLinks: "빠른 링크",
    about: "소개",
    contact: "연락처",
    contactTitle: "연락처",
    contactDescription: "메시지를 보내시면 최대한 빨리 답변 드리겠습니다.",
    emailPlaceholder: "이메일",
    messagePlaceholder: "메시지",
    send: "보내기",
    thankYou: "감사합니다! 메시지가 전송되었습니다.",
    copyright: "© {year} RightNowAid. 모든 권리 보유.",
    aboutName: "Nelson Rueda",
    aboutTitle: "소개",
    aboutRole: "소프트웨어 엔지니어 지망생, 창립자, & ELAC CS 학생",
    aboutDescription: "개인적 변화와 필요한 커뮤니티를 돕는 사명에 의해 추진—제 작업은 실제 세계의 어려움과 디지털 지원 도구 사이의 다리를 구축합니다.",
    aboutPoints: [
      "로스앤젤레스 동부의 6인 저소득 이민 가정에서 자랐으며, 식량, 퇴거, 주거 불안정을 직접 경험했습니다.",
      "공감에 의해 동기 부여되어, RightNowAid를 구축했습니다—사용자를 우편번호 지역의 긴급 지원과 연결하는 플랫폼입니다.",
      "프로그래밍, 풀스택 웹 개발 및 AI를 적극적으로 공부하고 있으며, UCLA CS로 전학 계획(2027년 가을), 2026년 Google-STEP 인턴십을 준비 중입니다."
    ]
  },
  AR: {
    tagline: "المساعدة الطارئة المدعومة بالذكاء الاصطناعي في متناول يدك.",
    quickLinks: "روابط سريعة",
    about: "حول",
    contact: "اتصل بنا",
    contactTitle: "اتصل بنا",
    contactDescription: "أرسل لي رسالة وسأرد عليك في أقرب وقت ممكن.",
    emailPlaceholder: "بريدك الإلكتروني",
    messagePlaceholder: "رسالتك",
    send: "إرسال",
    thankYou: "شكراً لك! تم إرسال رسالتك.",
    copyright: "© {year} RightNowAid. جميع الحقوق محفوظة.",
    aboutName: "Nelson Rueda",
    aboutTitle: "حول",
    aboutRole: "مهندس برمجيات طموح، مؤسس، وطالب علوم الحاسوب في ELAC",
    aboutDescription: "مدفوع بالتحول الشخصي ومهمة مساعدة المجتمعات المحتاجة—عملي يبني جسوراً بين تحديات العالم الحقيقي وأدوات الدعم الرقمية.",
    aboutPoints: [
      "نشأت في أسرة مهاجرة منخفضة الدخل مكونة من 6 أفراد في شرق لوس أنجلوس، واجهت انعدام الأمن الغذائي والطرد وعدم استقرار السكن مباشرة.",
      "مدفوعاً بالتعاطف، بنيت RightNowAid—منصة تربط المستخدمين بالمساعدة الطارئة في الرمز البريدي الخاص بهم.",
      "أدرس البرمجة وتطوير الويب الشامل والذكاء الاصطناعي بنشاط، أخطط للانتقال إلى UCLA CS (خريف 2027)، وأستعد بنشاط لتدريب Google-STEP 2026."
    ]
  }
};

const navigation = {
  main: [
    { name: 'About', href: '#' },
    { name: 'Features', href: '#' },
    { name: 'Blog', href: '#' },
    { name: 'Contact', href: '#' },
  ],
  social: [
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/in/nelson-rueda',
      icon: (props: any) => (
        <svg
          {...props}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={props.className || "h-5 w-5 sm:h-6 sm:w-6"}
        >
          <rect x="2" y="2" width="20" height="20" rx="4" />
          <path d="M8 11v5" />
          <path d="M8 8v.01" />
          <path d="M12 16v-5" />
          <path d="M16 16v-3a2 2 0 0 0-4 0" />
        </svg>
      ),
    },
    {
      name: 'GitHub',
      href: 'https://github.com/Nelson-228/RightNowAid',
      icon: Github,
    },
  ],
};

const Footer: React.FC = () => {
  const { selectedLanguage } = useLanguageStore();
  const [showContact, setShowContact] = React.useState(false);
  const [showAbout, setShowAbout] = React.useState(false);
  const [contactMessage, setContactMessage] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactSent, setContactSent] = React.useState(false);

  const t = translations[selectedLanguage as keyof typeof translations] || translations.EN;

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement email sending logic
    setContactSent(true);
  };

  return (
    <footer className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              RightNowAid
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t.tagline}
            </p>
            <div className="mt-4 flex space-x-6">
              {navigation.social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t.quickLinks}
            </h3>
            <ul className="mt-4 space-y-4">
              <li>
                <button
                  onClick={() => setShowAbout(true)}
                  className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  {t.about}
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowContact(true)}
                  className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  {t.contact}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 border-t border-gray-100 dark:border-gray-800 pt-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t.copyright.replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>
      </div>

      {/* Contact Modal */}
      {showContact && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm pt-8 sm:pt-0 sm:items-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 relative animate-scale-in">
            <button onClick={() => setShowContact(false)} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
              ×
            </button>
            <h2 className="text-xl font-bold text-foreground mb-2">{t.contactTitle}</h2>
            <p className="mb-4 text-muted-foreground">{t.contactDescription}</p>
            {contactSent ? (
              <div className="text-green-600 dark:text-green-400 font-semibold">{t.thankYou}</div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <input
                  type="email"
                  required
                  placeholder={t.emailPlaceholder}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  value={contactEmail}
                  onChange={e => setContactEmail(e.target.value)}
                />
                <textarea
                  required
                  placeholder={t.messagePlaceholder}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                  value={contactMessage}
                  onChange={e => setContactMessage(e.target.value)}
                />
                <button type="submit" className="btn btn-primary w-full">{t.send}</button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* About Modal */}
      {showAbout && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm pt-8 sm:pt-0 sm:items-center"
          onClick={() => setShowAbout(false)}
        >
          <div 
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-8 m-4 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowAbout(false)} 
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="flex flex-col items-center text-center">
              <img
                src="/profile.png"
                alt="Profile picture of Nelson Rueda"
                className="w-28 h-28 rounded-full object-cover mb-4 border-4 border-gray-200 dark:border-gray-700 shadow-lg"
              />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{t.aboutName}</h2>
              <p className="text-md font-medium text-teal-600 dark:text-teal-400 mt-1 mb-4">{t.aboutRole}</p>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-base">{t.aboutDescription}</p>
              <ul className="space-y-3 text-gray-600 dark:text-gray-400 text-left text-sm max-w-md">
                {t.aboutPoints.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-teal-500 mr-3 mt-1">&#10003;</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;